import { addDagen, fmtDag, weekdagIndex } from './datum';
import { Kalender } from './kalender';
import { kmSchatting, minutenVoorKm, reis, reisUren, REGIO_BONUS, REGIO_KM, type Coord } from './reis';
import type { Engineer, ISODate, Taak, TaakExpertise } from './types';

export interface SuggestieVraag {
  projectId?: string | null;
  expertises: TaakExpertise[];
  maxNiveau?: number | null;
  start: ISODate;
  dagen: number;
  urenPerDag: number;
  onsite: boolean;
  bestemming?: Coord | null;
  uitgezonderdTaakId?: string | null;
}

export interface Suggestie {
  engineerId: string;
  naam: string;
  score: number;
  kennis: number;
  mist: string[];
  vrij: number;
  past: number;
  nodig: number;
  reisMin: number | null;
  reisKm: number | null;
  /** kortste reistijd, rekening houdend met een andere afspraak in de buurt */
  reisMinBest: number | null;
  buurt: boolean;
  buurtKm: number | null;
  buurtKlant: string | null;
  alOpProject: boolean;
  problemen: string;
}

export function niveau(e: Engineer, expertise: string): number | null {
  const x = e.expertises.find((x) => x.naam === expertise);
  return x ? x.niveau || 100 : null;
}

/** Bestemming van een taak: afwijkend adres, anders projectlocatie. */
export type BestemmingFn = (t: Taak) => (Coord & { klant?: string | null }) | null;

/** SPEC §3.7 – slimme suggestie. */
export function suggesties(kal: Kalender, v: SuggestieVraag, bestemmingVan: BestemmingFn): Suggestie[] {
  const cfg = kal.cfg;
  const w = Math.max(0, Math.min(100, cfg.kennisWeging));
  // werkdagen van het team (ma–vr, geen feestdag), n dagen vanaf start
  const td: ISODate[] = [];
  for (let i = 0; td.length < Math.max(1, v.dagen) && i < 90; i++) {
    const d = addDagen(v.start, i);
    if (weekdagIndex(d) <= 4 && !kal.isHeleFeestdag(d)) td.push(d);
  }
  const uit: Suggestie[] = [];
  for (const e of kal.engineers) {
    if (e.inzet !== 'VOLLEDIG' || e.actief === false) continue;
    const namen = e.expertises.map((x) => x.naam);
    const pe = v.expertises.filter((x) => x.naam);
    // min-/maxniveau
    const minOK = pe.every((x) => !x.minNiveau || ((niveau(e, x.naam) ?? 0) >= x.minNiveau && namen.includes(x.naam)));
    const mx = v.maxNiveau ?? 100;
    const maxOK = mx >= 100 || pe.every((x) => !namen.includes(x.naam) || (niveau(e, x.naam) ?? 0) <= mx);
    if (!minOK || !maxOK) continue;

    const rs = v.onsite && v.bestemming ? reis(e, v.bestemming, cfg) : null;
    const dagInfo = td.map((d) => {
      const W = kal.isWerkdag(e, d);
      const A = kal.afwezigOp(e, d);
      const V = kal.vrij(e, d, v.uitgezonderdTaakId);
      // dichtstbijzijnde andere onsite-afspraak die dag
      let B: { k: number; kl: string | null } | null = null;
      if (v.onsite && v.bestemming) {
        for (const t of kal.takenOp(e.id, d, v.uitgezonderdTaakId)) {
          if (t.werkvorm !== 'ONSITE') continue;
          const b = bestemmingVan(t);
          if (!b) continue;
          const k = kmSchatting(b, v.bestemming, cfg);
          if (k == null) continue;
          if (!B || k < B.k) B = { k: Math.round(k), kl: b.klant ?? t.klant ?? t.projectnummer ?? null };
        }
      }
      const rdag = rs == null ? 0 : B ? Math.min(rs.min, minutenVoorKm(B.k, cfg)) : rs.min;
      return { d, W, A, V, B, rdag };
    });
    if (dagInfo.some((x) => !x.W)) continue; // Beschikbaar: geen niet-werkdagen in de periode

    const kennis = pe.length === 0 ? 100 : pe.reduce((s, x) => s + (niveau(e, x.naam) ?? 0), 0) / pe.length;
    const past = dagInfo.filter((x) => x.W && x.V >= v.urenPerDag + reisUren(x.rdag, cfg)).length;
    const alOpProject = !!v.projectId && kal.taken.some((t) => t.projectId === v.projectId && t.engineerId === e.id && t.status === 'GEPLAND' && t.id !== v.uitgezonderdTaakId);
    const buurtDagen = dagInfo.filter((x) => x.W && x.B);
    const buurt = buurtDagen.some((x) => x.B!.k <= REGIO_KM);
    const dichtst = buurtDagen.sort((a, b) => a.B!.k - b.B!.k)[0];
    const score =
      Math.round(kennis * (w / 100) + (past / Math.max(1, dagInfo.length)) * (100 - w)) +
      (alOpProject ? cfg.bonusProject : 0) +
      (buurt ? REGIO_BONUS : 0);
    const problemen = dagInfo
      .filter((x) => !x.W || x.V < v.urenPerDag + reisUren(x.rdag, cfg))
      .map((x) => {
        const v2 = Math.round(x.V * 100) / 100;
        const r = x.A ? x.A.soort.toLowerCase() : kal.isHeleFeestdag(x.d) ? 'feestdag' : !x.W ? 'werkt niet' : v2 <= 0 ? 'vol' : `maar ${String(v2).replace('.', ',')}u vrij`;
        return `${fmtDag(x.d)} ${r}`;
      })
      .join(', ');
    uit.push({
      engineerId: e.id,
      naam: e.naam,
      score,
      kennis: Math.round(kennis),
      mist: pe.filter((x) => !namen.includes(x.naam)).map((x) => x.naam),
      vrij: Math.round(dagInfo.filter((x) => x.W).reduce((s, x) => s + Math.max(0, x.V), 0) * 100) / 100,
      past,
      nodig: dagInfo.length,
      reisMin: rs?.min ?? null,
      reisKm: rs?.km ?? null,
      reisMinBest: rs == null ? null : Math.min(...dagInfo.filter((x) => x.W).map((x) => x.rdag)),
      buurt,
      buurtKm: dichtst?.B?.k ?? null,
      buurtKlant: dichtst?.B?.kl ?? null,
      alOpProject,
      problemen,
    });
  }
  return uit.sort((a, b) => b.score * 1000 + b.vrij - (a.score * 1000 + a.vrij)).slice(0, Math.max(1, cfg.aantalSuggesties));
}
